// src/context/CompanyContext.jsx
import { createContext, useContext, useEffect, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

const CompanyContext = createContext(null);

export function CompanyProvider({ children }) {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchCompany = async () => {
    const user      = JSON.parse(localStorage.getItem("user") || "{}");
    const companyId = user.company_id || "";

    if (!companyId) { setLoading(false); return; }

    try {
  // D-303 (C-4): companies read now needs the company_id claim. On a
  // brand-new user's first login syncUserClaims may still be running —
  // wait (max ~6 s) until the fresh token carries this company_id.
  const { getAuth } = await import("firebase/auth");
  const authInstance = getAuth();
  if (authInstance.currentUser) {
    for (let i = 0; i < 5; i++) {
      const tok = await authInstance.currentUser.getIdTokenResult(true);
      if (tok.claims.company_id === companyId || tok.claims.is_super_admin === true) break;
      await new Promise(res => setTimeout(res, 1500));
    }
  }

  const snap = await getDoc(doc(db, "companies", companyId));
  if (snap.exists()) {
    setCompany({ id: snap.id, ...snap.data() });
  } else {
    // NEVER create placeholder — company already exist karti hai
    console.warn("Company doc not found for:", companyId);
    setCompany({
      id:           companyId,
      company_id:   companyId,
      company_name: "My Company",
    });
  }
} catch (err) {
  console.error("CompanyContext fetch error:", err);
  // Retry once after token refresh
  try {
    const { getAuth } = await import("firebase/auth");
    const authInstance = getAuth();
    if (authInstance.currentUser) {
      await authInstance.currentUser.getIdToken(true);
    }
    const snap = await getDoc(doc(db, "companies", companyId));
    if (snap.exists()) {
      setCompany({ id: snap.id, ...snap.data() });
      return;
    }
  } catch (retryErr) {
    console.error("CompanyContext retry error:", retryErr);
  }
  setCompany({
    id:           companyId,
    company_id:   companyId,
    company_name: "My Company",
  });
} finally {
  setLoading(false);
}
  };

  useEffect(() => {
    fetchCompany();
    window.addEventListener("userUpdated", fetchCompany);
    window.addEventListener("storage", fetchCompany);
    return () => {
      window.removeEventListener("userUpdated", fetchCompany);
      window.removeEventListener("storage", fetchCompany);
    };
  }, []);

  const refreshCompany = () => fetchCompany();

  return (
    <CompanyContext.Provider value={{ company, loading, refreshCompany }}>
      {children}
    </CompanyContext.Provider>
  );
}

// Default modules — sab on
export const DEFAULT_MODULES = {
  service:     true,
  inventory:   true,
  procurement: true,
  billing:     true,
  accounts:    true,
  projects:    true,
  assets:      true,
  amc:         true,
  reports:     true,
  transport:   true,
  expenses:    true,
};

export function useCompany() {
  const ctx = useContext(CompanyContext);
  if (!ctx) throw new Error("useCompany must be used inside CompanyProvider");
  return ctx;
}

// Helper — check if module enabled
export function useModule(moduleName) {
  const { company } = useCompany();
  const modules = company?.modules || DEFAULT_MODULES;
  return modules[moduleName] !== false; // default true agar set nahi
}